import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import api from '../../../api';

import { fetchRecycleBin } from './recycleBinSlice';
import { fetchCoachesMin } from '../optionsSlice';

// Async thunk for fetching clients
export const fetchClients = createAsyncThunk('clients/fetchClients', async ({ page, account_type = 'client', searchTerm = '', activeCoach = '', user_type ='' }) => {
  const response = await api.get(`${process.env.REACT_APP_API_BASE_URL}/admin/users-list`, {
    params: {
      page,
      account_type,
      searchTerm,
      active_coach:activeCoach,
      user_type:user_type
    },
  });
  return response.data;
});

// Async thunk for exporting clients
export const exportClients = createAsyncThunk('clients/exportClients', async (type) => {
  const response = await api.get(`${process.env.REACT_APP_API_BASE_URL}/admin/export?tab=`+type, {
    responseType: 'blob', // Important for downloading files
  });

  // Create a URL for the file
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', type+'.csv'); // Set the file name for download
  document.body.appendChild(link);
  link.click(); // Trigger the download
  document.body.removeChild(link);
});

// Async thunk for creating a client
export const createClient = createAsyncThunk('clients/createClient', async (clientData, { dispatch }) => {
  const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/admin/create-user`, clientData);
   if (response.data.success) {
    // Fetch the updated list of clients after successful update
    dispatch(fetchClients({ page: clientData.page })); // Pass the appropriate page number if needed
  }

  return response.data;
});

// Async thunk for updating a client
export const updateClient  = createAsyncThunk('clients/updateClient', async (clientData, { dispatch }) => {
  const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/admin/update-user/${clientData.id}`, clientData);  
  if (response.data.success) {
    // Fetch the updated list of clients after successful update
    dispatch(fetchClients({ page: clientData.page })); // Pass the appropriate page number if needed
  }
  return response.data;
});

// Thunk for assigning a coach
export const assignCoach = createAsyncThunk('clients/assignCoach', async ({ userId, coachId }, { dispatch }) => {
  const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/admin/assign-coach`, {
    user_id: userId,
    coach_id: coachId,
  }); 
  dispatch(fetchClients({ page: 1 }));  
  return response.data;
});

export const deleteClient  = createAsyncThunk('clients/deleteClient', async ({clientData, id, currentPage}, { dispatch }) => {
  try {
   const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/admin/users-delete`, { id });
	dispatch(fetchClients({ page: currentPage, account_type: 'client'  }));	
	dispatch(fetchRecycleBin(1));
  dispatch(fetchCoachesMin());
	return response.data;
  } catch (error) {
    console.error("Error deleting client:", error);
    throw error; // Optionally handle the error if needed
  }
});

// Thunk for assigning a coach
export const getProgramListMinimal = createAsyncThunk('clients/getProgramListMinimal', async () => {
  const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/admin/training-programs-list?minimal=true`); 
  return response.data;
});


// Slice for clients
const clientsSlice = createSlice({
  name: 'admin_clients',
  initialState: {
    users: [],
    currentPage: 1,
    totalPages: 1,
    loading: false,
    successMessage: '', // For success messages
    errorMessage: '',   // For error messages
    programs_minimal: []
  },
  reducers: {
    clearMessages: (state) => {
      state.successMessage = '';
      state.errorMessage = '';
      state.programs_minimal = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClients.pending, (state) => {
        state.loading = true;
        state.errorMessage = ''; // Clear error message on new fetch
      })
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.users = action.payload.data.data;
        state.currentPage = action.payload.data.current_page || 1;
        state.totalPages = action.payload.data.last_page || 0;
        state.loading = false;
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message;
      })
      .addCase(getProgramListMinimal.pending, (state) => {
        state.loading = true;
        state.errorMessage = '';
      })
      .addCase(getProgramListMinimal.fulfilled, (state, action) => {
        state.programs_minimal = action.payload.data;
        state.loading = false;
      })
      .addCase(getProgramListMinimal.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message;
      })
      .addCase(exportClients.fulfilled, (state) => {
        // Handle any additional state updates after export if needed
      })
      .addCase(createClient.fulfilled, (state, action) => {
        if (action.payload.success === true) {
          state.successMessage = 'Client created successfully!';
        } else {
          state.errorMessage = action.payload.message;
        }
      })
      .addCase(createClient.rejected, (state, action) => {
        state.errorMessage = action.error.message; // Capture error message
      })
      .addCase(updateClient.fulfilled, (state, action) => {
        if (action.payload.success === true) {
          state.successMessage = 'Client updated successfully!';
        } else {
          state.errorMessage = action.payload.message;
        }
      })
      .addCase(updateClient.rejected, (state, action) => {
        state.errorMessage = action.error.message; // Capture error message
      })
      .addCase(deleteClient.fulfilled, (state, action) => {        
      })
      .addCase(assignCoach.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.successMessage = action.payload.message;
        }
      })
      .addCase(assignCoach.rejected, (state, action) => {
        state.errorMessage = action.error.message;
      });
  },
});

export const { clearMessages } = clientsSlice.actions;
export default clientsSlice.reducer;